import { DropdownListItem } from "components/DesignComponents/InputFieldWithDropdown/types";
import { NOT_FILTER_NAMES, QueryParams, TABLE_NAMES } from "shared";

import { QUERY_FIELDS } from "../types";

const useFields = (
    tableName: TABLE_NAMES,
    isInnerTable: boolean,
    innerTableQueryParams: QueryParams,
    currentParams: QueryParams
) => {
    const TRANSLATION_KEYS = {
        CODE: "code",
        DESCRIPTION: "description",
        EMAIL: "email",
        ICCID: "iccid",
        ID: "id",
        IMEI: "imei",
        MODEL: "model",
        NAME: "name",
        PRODUCT_CODE: "product code",
        ROLE_NAME: "role name",
        SERIAL: "serial"
    };

    const {
        CODE,
        DESCRIPTION,
        EMAIL,
        ICCID,
        ID,
        IMEI,
        MODEL,
        NAME,
        PRODUCT_CODE,
        ROLE_NAME,
        SERIAL
    } = TRANSLATION_KEYS;

    // The first item of each array is the default query field
    const ALL_QUERY_FIELDS: Partial<Record<TABLE_NAMES, DropdownListItem[]>> = {
        [TABLE_NAMES.Companies]: [
            {
                value: QUERY_FIELDS.Name,
                name: NAME
            },
            {
                value: QUERY_FIELDS.Code,
                name: CODE
            },
            { value: QUERY_FIELDS.Id, name: ID }
        ],
        [TABLE_NAMES.DesktopLicenses]: [
            {
                value: QUERY_FIELDS.Name,
                name: NAME
            }
        ],
        [TABLE_NAMES.DeviceModels]: [
            {
                value: QUERY_FIELDS.Name,
                name: MODEL
            }
        ],
        [TABLE_NAMES.DeviceModelTaskCompatibilities]: [
            { value: QUERY_FIELDS.Name, name: MODEL }
        ],
        [TABLE_NAMES.Devices]: [
            {
                value: QUERY_FIELDS.Imei,
                name: IMEI
            },
            {
                value: QUERY_FIELDS.Description,
                name: DESCRIPTION
            },
            {
                value: QUERY_FIELDS.Serial,
                name: SERIAL
            },
            {
                value: QUERY_FIELDS.IccId,
                name: ICCID
            }
        ],
        [TABLE_NAMES.Files]: [
            {
                value: QUERY_FIELDS.Name,
                name: NAME
            },
            {
                value: QUERY_FIELDS.Description,
                name: DESCRIPTION
            },
            { value: QUERY_FIELDS.Id, name: ID }
        ],
        [TABLE_NAMES.DeviceGroups]: [
            {
                value: QUERY_FIELDS.Name,
                name: NAME
            },
            { value: QUERY_FIELDS.Id, name: ID }
        ],
        [TABLE_NAMES.Permissions]: [
            {
                value: QUERY_FIELDS.Name,
                name: NAME
            }
        ],
        [TABLE_NAMES.ProductCodes]: [
            {
                value: QUERY_FIELDS.ProductCode,
                name: PRODUCT_CODE
            }
        ],
        [TABLE_NAMES.Roles]: [{ value: QUERY_FIELDS.Name, name: ROLE_NAME }],
        [TABLE_NAMES.Tasks]: [
            {
                value: QUERY_FIELDS.Imei,
                name: IMEI
            }
        ],
        [TABLE_NAMES.Users]: [
            {
                value: QUERY_FIELDS.Email,
                name: EMAIL
            }
        ]
    };

    const activeQueryFields = ALL_QUERY_FIELDS[tableName] ?? [];
    const storageKeyName = `${tableName}QueryField`;

    const getActiveField = (
        currentQueryField: QUERY_FIELDS | null | undefined
    ) => activeQueryFields.find(field => field.value === currentQueryField);

    const setActiveQueryField = () => {
        // Current query field
        const currentQueryField = (
            isInnerTable
                ? innerTableQueryParams[NOT_FILTER_NAMES.QueryField]
                : currentParams[NOT_FILTER_NAMES.QueryField]
        ) as QUERY_FIELDS | undefined;

        const activeCurrentQueryField = getActiveField(currentQueryField);

        if (activeCurrentQueryField) {
            localStorage.setItem(storageKeyName, activeCurrentQueryField.value);
            return activeCurrentQueryField;
        }

        // Stored in local storage query field
        const storedQueryField = localStorage.getItem(
            storageKeyName
        ) as QUERY_FIELDS | null;

        const activeStoredQueryField = getActiveField(storedQueryField);
        if (activeStoredQueryField) return activeStoredQueryField;

        // Default query field - first in list
        const firstQueryField = activeQueryFields[0];
        localStorage.setItem(storageKeyName, firstQueryField.value);
        return firstQueryField;
    };

    return {
        activeQueryFields,
        storageKeyName,
        setActiveQueryField
    };
};

export { useFields };
